import { useState, useEffect, useRef } from "react";
import logo from "./images/logoblanco2.svg";
import appa from "./images/appa.png";
import calcifer from "./images/calcifer.png";
import moby from "./images/mobydick.jpeg";
import casitas from "./images/casitas.jpg";
import gatoazul from "./images/gatoazul.jpeg";
import ignis from "./images/ignis.png";
import logoNegro from "./images/logonegro_animado-2.svg";
import videoTaller from "./images/videotaller.mp4";
import tallerFoto from "./images/taller-foto.jpeg"; // ← cambia por el nombre real de tu foto

const INSTAGRAM_URL =
  "https://instagram.com/lilyclayandtales?igsh=MTVndXd1eW55Zjdhag%3D%3D&utm_source=qr";

export default function App() {
  const [showMenu, setShowMenu] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [etsyMessage, setEtsyMessage] = useState(false);

  const logoNegroRef = useRef<HTMLDivElement>(null);

  // Reveal on scroll + logo del gato + logo negro por IntersectionObserver
  useEffect(() => {
    const revealElements = document.querySelectorAll(".reveal");

    const revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.15 }
    );
    revealElements.forEach((el) => revealObserver.observe(el));

    // Logo negro: se dibuja en cuanto el borde superior asoma por pantalla
    const negroObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const container = entry.target as HTMLElement;
            const obj = container.querySelector("object") as HTMLObjectElement | null;
            if (obj) {
              const triggerDraw = () => {
                const svgDoc = obj.contentDocument;
                const svgEl = svgDoc?.getElementById("logo-draw");
                if (svgEl) svgEl.classList.add("drawing");
              };
              if (obj.contentDocument?.readyState === "complete") {
                triggerDraw();
              } else {
                obj.addEventListener("load", triggerDraw, { once: true });
              }
            }
            negroObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0, rootMargin: "0px 0px -40px 0px" }
    );
    if (logoNegroRef.current) negroObserver.observe(logoNegroRef.current);

    return () => {
      revealObserver.disconnect();
      negroObserver.disconnect();
    };
  }, []);

  // Menú flotante
  useEffect(() => {
    const handleScroll = () => setShowMenu(window.scrollY > 300);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Cerrar lightbox con Escape
  useEffect(() => {
    if (!selectedImage) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSelectedImage(null);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [selectedImage]);

  return (
    <div>
      <header className={`menu ${showMenu ? "visible" : ""}`}>
        <nav className="menu-nav">
          <a href="/">Inicio</a>
          <a href="#taller">Taller</a>
          <a href="#piezas">Piezas</a>
          <a href="#contacto">Contacto</a>
        </nav>
      </header>

      {/* ── HERO ── */}
      <section className="hero">
        <video className="hero-video" autoPlay muted loop playsInline>
          <source src={videoTaller} type="video/mp4" />
        </video>
        <div className="hero-overlay" />
        <div className="hero-content">
          <img src={logo} alt="Lily Clay & Tales" className="hero-logo" />
          <p className="hero-eyebrow">SLOWLY MADE IN SPAIN</p>
          <h1>Lily Clay & Tales</h1>
          <p className="hero-subtitle">Cerámica hecha a mano.</p>
          <p className="hero-text">Historias que empiezan en el barro.</p>
          <div className="hero-buttons">
            <a href="#piezas">Ver piezas</a>
            <a href="#taller" className="secondary">Conocer el taller</a>
          </div>
        </div>
      </section>

      {/* ── TALLER — solo presentación ── */}
      <section id="taller" className="taller-section reveal">
        <div className="sobremi">
          <div className="sobremi-imagen-wrap">
            <img
              src={tallerFoto}
              alt="Laura trabajando en su taller de cerámica"
              className="sobremi-imagen"
            />
          </div>
          <div className="sobremi-texto">
            <p className="section-eyebrow">EL TALLER</p>
            <h2>Hola, soy Laura</h2>
            <p className="taller-text">
              La cerámica entró en mi vida como un refugio. Un lugar donde
              descansar de la velocidad del día a día y volver a algo lento,
              táctil, real.
            </p>
            <p className="taller-text">
              Con el tiempo se convirtió en algo más: una forma de expresarme y
              de conectar con otras personas. Me devolvió a una parte de mí que
              creía haber dejado atrás — esa niña que creaba por el puro placer
              de crear.
            </p>
            <p className="taller-text sobremi-cierre">
              Hoy, con las manos llenas de barro, siento que estoy cumpliendo
              los sueños que me prometí. Y eso lo cambia todo.
            </p>
            <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" className="sobremi-cta">
              Sígueme en Instagram →
            </a>
          </div>
        </div>
      </section>

      {/* ── TRANSICIÓN ── */}
      <div className="transicion">
        <p>El barro también limpia.</p>
      </div>

      {/* ── PIEZAS ── */}
      <section id="piezas" className="piezas reveal">
        <div className="piezas-header">
          <p className="section-eyebrow">PIEZAS</p>
        </div>
        <div className="grid">
          <div className="card">
            <img src={calcifer} alt="Cuenco Cálcifer, cerámica artesanal pintada a mano inspirada en El castillo ambulante" className="pieza-img" onClick={() => setSelectedImage(calcifer)} />
            <h3>Cálcifer</h3>
            <p>"May all your bacon burn"<br />Cuenco para ovillo de lana.</p>
            <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" className="card-cta">Encargar en Instagram →</a>
          </div>
          <div className="card">
            <img src={moby} alt="Maceta Moby Dick, cerámica artesanal con relieve de ballena, 8cm de diámetro" className="pieza-img" onClick={() => setSelectedImage(moby)} />
            <h3>Maceta Moby Dick</h3>
            <p>"No sé lo que vendrá, pero sea lo que sea, iré a su encuentro riendo."<br />Maceta 8cm de diámetro.</p>
            <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" className="card-cta">Encargar en Instagram →</a>
          </div>
          <div className="card">
            <img src={appa} alt="Taza Appa, cerámica artesanal inspirada en el bisonte volador de Avatar" className="pieza-img" onClick={() => setSelectedImage(appa)} />
            <h3>Taza Appa</h3>
            <p>"Yip Yip"<br />Taza de Appa, compañero del último airbender.</p>
            <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" className="card-cta">Encargar en Instagram →</a>
          </div>
          <div className="card">
            <img src={gatoazul} alt="Adorno de gato azul, figura decorativa de cerámica artesanal" className="pieza-img" onClick={() => setSelectedImage(gatoazul)} />
            <h3>Adorno Gato</h3>
            <p>"Miau Miau Miau"<br />Adornito porque sí.</p>
            <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" className="card-cta">Encargar en Instagram →</a>
          </div>
          <div className="card">
            <img src={casitas} alt="Pueblo navideño de cerámica, casitas artesanales para velas navideñas" className="pieza-img" onClick={() => setSelectedImage(casitas)} />
            <h3>Pueblo navideño</h3>
            <p>"Have yourself a merry little Xmas."<br />Casitas para velas navideñas.</p>
            <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" className="card-cta">Encargar en Instagram →</a>
          </div>
          <div className="card">
            <img src={ignis} alt="Platito de gatito Ignis, plato decorativo de cerámica artesanal, 15cm de diámetro" className="pieza-img" onClick={() => setSelectedImage(ignis)} />
            <h3>Platito de gatito</h3>
            <p>"Creo que he visto un lindo gatito."<br />Plato 15cm de diámetro.</p>
            <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" className="card-cta">Encargar en Instagram →</a>
          </div>
        </div>
      </section>

      {/* ── LOGO NEGRO ANIMADO (antes del contacto) ── */}
      <div className="footer-logo" ref={logoNegroRef}>
        <object
          type="image/svg+xml"
          data={logoNegro}
          aria-label="Lily Clay & Tales logo"
          className="logo-negro-obj"
        />
      </div>
      <div className="footer-divider" />

      {/* ── CONTACTO ── */}
      <section id="contacto" className="contacto reveal">
        <div className="contacto-container">
          <p className="section-eyebrow">CONTACTO</p>
          <p className="contacto-text">
            Si quieres seguir el proyecto, ver nuevas piezas o escribirme,
            puedes encontrarme aquí.
          </p>
          <div className="contacto-links">
            <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer"><span>Instagram</span></a>
            <a href="mailto:lilyclayandtales@gmail.com"><span>Email</span></a>
            <a href="#" onClick={(e) => { e.preventDefault(); setEtsyMessage(true); setTimeout(() => setEtsyMessage(false), 2500); }}>
              <span>Etsy</span>
            </a>
          </div>
        </div>
      </section>

      {/* ── LIGHTBOX ── */}
      {selectedImage && (
        <div className="lightbox" onClick={() => setSelectedImage(null)} role="dialog" aria-modal="true" aria-label="Vista ampliada de la pieza">
          <button className="lightbox-close" onClick={() => setSelectedImage(null)} aria-label="Cerrar imagen">✕</button>
          <img src={selectedImage} alt="Vista ampliada de la pieza cerámica" />
        </div>
      )}

      {etsyMessage && (
        <div className="etsy-popup">La tienda aún está en el horno 🔥</div>
      )}
    </div>
  );
}
